import type { DateTime } from 'luxon'

import type Reservation from '@/assets/classes/parking/Reservation'
import type Vehicle from '@/assets/classes/parking/Vehicle'

type PartOfDayCount = {
  [key in PartOfDay]: number
}

export enum PartOfDay {
  morning = 'morning',
  afternoon = 'afternoon',
  allday = 'allday',
}

export enum ErrorCodes {
  noAvailableSlots = 'NO_AVAILABLE_SLOTS',
  sameTimeReservation = 'SAME_TIME_RESERVATION',
  reservationsNotAllowed = 'RESERVATIONS_NOT_ALLOWED',
  resInPast = 'RESERVATION_IN_PAST',
  resInFuture = 'RESERVATION_IN_FUTURE',
  todayAlldayExits = 'TODAY_ALLDAY_EXISTS',
  badPartOfDayToday = 'CANNOT_RESERVE_ANOTHER_PART_OF_DAY_TODAY',
  reservationsLimitReached = 'RESERVATIONS_LIMIT_REACHED',
  vehicleMustExist = 'VEHICLE_MUST_EXIST',
  ecvAlreadyInUse = 'ECV_ALREADY_IN_USE',
  dateInPast = 'DATE_IN_PAST',
  dateTooFar = 'DATE_TOO_FAR',
  missingCardReturned = 'MISSING_CARD_RETURNED',
  missingSpecification = 'MISSING_SPECIFICATION',
  roleConflict = 'ROLE_CONFLICT',
  databaseError = 'DATABASE_ERROR',
  permissionDenied = 'PERMISSION_DENIED',
  userBanned = 'USER_BANNED',
  cardLoanNotFound = 'CARD_LOAN_NOT_FOUND',
  invalidCardLoanId = 'INVALID_CARD_LOAN_ID',
  cardReturnVerificationRequired = 'CARD_RETURN_VERIFICATION_REQUIRED',
}

export interface ReservationData {
  id: number
  date: string
  partOfDay: PartOfDay
  cardReturned: boolean
  cancelled?: boolean
}

export interface CardLoanData {
  id: string
  reservationId: number
  date: string
  partOfDay: PartOfDay
  cancelled: boolean
  returnDueAt: string
  verificationPolicy: 'self_declared_v1' | 'nfc_required_v1'
}

export interface CardLoanList {
  rows: CardLoanData[]
  total: number
  limit: number
  offset: number
}

export interface ReservationsResponse {
  total: number
  limit: number
  offset: number
  rows: ReservationData[]
}

export interface DefaultSettings {
  maxAllowedResDate: number
  hoursFrameReturnCard: number
  latestFeBreakingChangeVersion: string
}

// Raw ban status as returned by GET /parking/ban.
export interface BanResponse {
  bannedUntil: string | null
  reason: string | null
}

// Ban status kept in the store (bannedUntil parsed to a DateTime for the UI).
export interface BanInfo {
  bannedUntil: DateTime
  reason: string | null
}

// @ts-ignore
export interface ReservationRich extends ReservationData {
  date: DateTime | null
  timelineDate: {
    day: string
    month: string
  }
  partOfDay: {
    name: string
    range: string[]
  }
}

export interface AvailableSlots extends PartOfDayCount {
  date: string
  isAnyAvailable: boolean
  isMorningAvailable: boolean
  isAfternoonAvailable: boolean
  isAlldayAvailable: boolean
  values: PartOfDay[]
  displayValues: string[]
}

export interface SelectedSlotData {
  date: DateTime | string
  time: string
}

export interface User {
  id: string
  name: string
  registration: string
  email: string
  prefs: Record<string, unknown>
  companyId?: string
  companyName?: string
}

export interface StoreState {
  defaultSettings: DefaultSettings | object
  isUserLoggedIn: boolean
  reservations: Reservation[]
  historicalReservations: Reservation[]
  cardMissingReservations: Reservation[]
  cardLoans: CardLoanData[]
  cardLoanTotal: number
  vehicles: Vehicle[]
  user: User | null
  banInfo: BanInfo | null
  updateRequired: boolean
}

export interface ParkingReservationSuccessEvent {
  date: string
  partOfDay: PartOfDay
}

export interface VehicleRequest {
  ecv: string
  brand: string | null
  model: string | null
  color: string | null
}

export interface VehicleResponse extends VehicleRequest {
  id: number
  logoUrl: string | null
}
